<script lang="ts" setup>
import { ref, watch, computed, onMounted } from "vue";
import { useI18n } from 'vue-i18n';
import TagInputSelectWidget from '@/components/FormWidget/TagInputSelectWidget/index.vue';
import { useConfigStore } from '@/stores/config';
import { usePaymentStore } from '@/stores/payment';
import { getCurrencySymbol } from '@/utils/formatNumber';
import { IPaymentMethod } from '@posx/core';

interface Tag {
  label: string;
  value: string;
}

const { t } = useI18n();
const configStore = useConfigStore();
const paymentStore = usePaymentStore();

const $emit = defineEmits(["change", "update:modelValue", "close", "confirm"]);
const $props = defineProps({
  modelValue: {
    type: Boolean,
  },
  data: {
    type: Object,
    defualt: 0,
  },
  type: {
    type: String,
  },
});

const number = ref(0);
const note = ref<any>([]);
const visible = ref($props.modelValue);
const selectedPaymentMethod = ref<IPaymentMethod | null>(null);

// Payment methods available for pay in/out (non-disabled)
const paymentMethods = computed(() => paymentStore.selecteds);
// Default to cash payment method
const defaultPaymentMethod = computed(() => paymentMethods.value.find(pm => pm.is_cash) || paymentMethods.value[0] || null);

// Get pay in/out reasons from config, converted to Tag objects for TagInputSelectWidget
const reasonOptions = computed(() => {
  const storage = configStore.appConfig?.storage;
  const reasons = $props.type === 'IN' ? (storage?.pay_in_reasons || []) : (storage?.pay_out_reasons || []);
  return reasons.map((reason: string) => ({ label: reason, value: reason }));
});
watch(
  () => $props.modelValue,
  (newVal: boolean) => {
    visible.value = newVal;
    if (newVal) {
      number.value = 0;
      note.value = [];
      selectedPaymentMethod.value = defaultPaymentMethod.value;
    }
  }
);

onMounted(() => paymentStore.initialize()); // start liveQuery so payment methods list is populated

// Persist new tag to cloud storage
const handleCreateNewTag = async (newTag: Tag) => {
  const appConfig = configStore.appConfig;
  if (!appConfig) return;
  const storageKey = $props.type === 'IN' ? 'pay_in_reasons' : 'pay_out_reasons';
  const currentList = appConfig.storage?.[storageKey] || [];
  if (currentList.includes(newTag.label)) return;
  await configStore.updateStorage(storageKey, [newTag.label, ...currentList]);
};

const handleClose = () => {
  $emit("change", false);
  $emit("update:modelValue", false);
  $emit("close", false);
};

const handleConfirm = () => {
  const selectedReasons = note.value.join(', ');
  $emit("confirm", number.value, selectedReasons, selectedPaymentMethod.value);
};
</script>

<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="$props.type === 'IN' ? t('pay.title.payIn') : t('pay.title.payOut')"
    :before-close="handleClose"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="w-[355px] flex flex-col justify-between">
      <div>
        <div class="mb-[5px]">
          <span class="text-[14px] text-[#3a3a3a] mb-2 block">{{ t('payment.paymentMethod') }}</span>
          <el-select v-model="selectedPaymentMethod" value-key="uid" class="w-full">
            <el-option v-for="pm in paymentMethods" :key="pm.uid" :label="pm.name" :value="pm" />
          </el-select>
        </div>
        <x-input :label="getCurrencySymbol()" v-model="number" class="mb-[5px]" />
        <div class="mb-[5px]">
          <span class="text-[14px] text-[#3a3a3a] mb-2 block">{{ t('pay.placeholder') }}</span>
          <TagInputSelectWidget
            v-model="note"
            :placeholder="t('pay.placeholder')"
            :options="reasonOptions"
            class="w-full"
            @create="handleCreateNewTag"
          />
        </div>
      </div>
      <div>
        <x-keyboard class="mb-[5px]" v-model="number" />
        <x-button type="primary" class="w-full" :debounceTime="2000" @click="handleConfirm">{{
          t("common.confirm")
        }}</x-button>
      </div>
    </div>
  </x-dialog>
</template>

